// react
import { useEffect, useMemo } from 'react';
import styled from 'styled-components';

// styles
import { css } from 'styled-components';

// components
import {
  GenericFlowProps,
  OnboardingActions,
  OnboardingActionType,
  toGenericOnboarding,
} from '@/components/pages';
import { Container, Icon } from '@/components/atoms';
import { Editor } from '@/components/organisms/Editor/Editor';
import {
  createFieldHelper,
  FieldDef,
} from '@/components/organisms/EditorFields/fieldHelper';
import Tooltip from '@/components/atoms/Tooltip/Tooltip';

export const OnboardingEditor = toGenericOnboarding(
  ({
    currentIndex = 0,
    config,
    isNextDisabled,
    setIsNextDisabled,
    handleClick,
  }: GenericFlowProps<OnboardingEditorProps>) => {
    // memos
    const currentConfig = useMemo(
      () => config[currentIndex],
      [config, currentIndex],
    );

    const fields = useMemo(() => {
      return createFieldHelper<{ value: string }>({
        value: currentConfig?.fieldDef,
      });
    }, [currentConfig]);

    const value = useMemo(() => currentConfig?.value, [currentConfig]);
    const setValue = useMemo(() => currentConfig?.onChange, [currentConfig]);
    const size = useMemo(() => currentConfig?.size || 'lg', [currentConfig]);

    // effects
    useEffect(() => {
      if (!config?.[currentIndex]?.fieldDef?.validation) {
        setIsNextDisabled(false);
        return;
      }
      setIsNextDisabled(
        !config?.[currentIndex]?.fieldDef?.validation?.(value?.value) || false,
      );
    }, [value, currentIndex, config]);

    return (
      <StyledContainer size='md' isScroll>
        <Wrapper>
          {config && (
            <>
              <Title $size={size}>{currentConfig?.title}</Title>
              <Description>
                <Text $size={size}>{currentConfig?.subTitle}</Text>
                {currentConfig?.tooltip && (
                  <StyledTooltip content={currentConfig?.tooltip}>
                    <TooltipIcon>
                      <Icon icon={'info-circle'} fontSize={24} />
                    </TooltipIcon>
                  </StyledTooltip>
                )}
              </Description>
              {fields && (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    !isNextDisabled && handleClick?.(OnboardingActionType.Next);
                  }}
                >
                  <Editor
                    fields={fields}
                    value={value}
                    onChange={setValue}
                    isFloatingActions={false}
                    isActionsDisabled={true}
                    isForm={false}
                  />
                </form>
              )}
              {currentConfig?.isDisclaimer && (
                <Disclaimer>
                  By continuing to use this app you agree to our{' '}
                  <a
                    href='https://www.karehero.com/privacy-policy'
                    target='_blank'
                  >
                    privacy policy
                  </a>
                </Disclaimer>
              )}
            </>
          )}
        </Wrapper>
      </StyledContainer>
    );
  },
);

const Disclaimer = styled.div`
  color: ${(props) => props.theme.color.neutral[80]};
  font: ${(props) => props.theme.typography.body.sm.regular};
  padding-top: ${(props) => props.theme.spacing[24]};
  text-align: center;
`;

const StyledContainer = styled(Container)`
  height: 100%;
  background: ${(props) => props.theme.color.neutral[10]};
`;

const Wrapper = styled.div`
  display: flex;
  min-height: content;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  gap: ${(props) => props.theme.spacing[16]};
  padding: ${(props) => props.theme.spacing[32]} 0;
`;

interface SizeProps {
  $size: Size;
}

const Title = styled.div<SizeProps>`
  width: 100%;
  color: ${(props) => props.theme.color.primary[80]};
  ${(props) => {
    switch (props.$size) {
      case 'sm':
        return css`
          font: ${props.theme.typography.heading['2xl']};
        `;
      case 'lg':
        return css`
          font: ${props.theme.typography.heading['3xl']};
        `;
    }
  }};

  ${(props) =>
    props.theme.breakpoint.md(css`
      ${() => {
        switch (props.$size) {
          case 'sm':
            return css`
              font: ${props.theme.typography.heading['3xl']};
            `;
          case 'lg':
            return css`
              font: ${props.theme.typography.heading['6xl']};
            `;
        }
      }};
    `)}
`;

const Description = styled.div`
  display: flex;
  gap: ${(props) => props.theme.spacing[8]};
  align-items: flex-end;
`;

const Text = styled.div<SizeProps>`
  color: ${(props) => props.theme.color.neutral[90]};
  ${(props) => {
    switch (props.$size) {
      case 'sm':
        return css`
          font: ${props.theme.typography.body.sm.regular};
        `;
      case 'lg':
        return css`
          font: ${props.theme.typography.body.sm.regular};
        `;
    }
  }};

  ${(props) =>
    props.theme.breakpoint.md(css`
      ${() => {
        switch (props.$size) {
          case 'sm':
            return css`
              font: ${props.theme.typography.body.md.regular};
            `;
          case 'lg':
            return css`
              font: ${props.theme.typography.body.xl.regular};
            `;
        }
      }};
    `)}
`;

const StyledTooltip = styled(Tooltip)``;

const TooltipIcon = styled.div`
  color: ${(props) => props.theme.color.neutral[70]};
`;

type Size = 'sm' | 'lg';

export interface OnboardingEditorProps extends OnboardingActions {
  title: string;
  subTitle: string;
  tooltip?: string;
  value: any;
  onChange: (value: string) => void;
  fieldDef: FieldDef;
  isDisclaimer?: boolean;
  size?: Size;
}

OnboardingEditor.displayName = 'OnboardingEditor';

export default toGenericOnboarding(OnboardingEditor);
